/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogTagFragment, CosmosAssetFragment } from "@cosmos/hygraph-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CosmosTagsAndAttribution, CosmosImage, CosmosTagProps } from "@components/elements";
import { useDisabledHygraphImageLoader, useHygraphImageLoader } from "@components/hooks";
import { css, cx } from "@panda/css";

// Fallback poster is only visible briefly before the video plays. 640px is enough for
// mobile and readable on desktop hero contexts; avoids downloading a 1200px+ still frame.
const POSTER_WIDTH = 640;

export type CosmosVideoProps = {
	video?: CosmosAssetFragment | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	className?: string;
	attributionClassName?: string;
	disableAutoplay?: boolean;
	hideAttribution?: boolean;
	mediaRef?: (el: HTMLVideoElement | null) => void;
	crossOrigin?: "" | "anonymous" | "use-credentials";
};

export const CosmosVideo = ({
	className,
	video,
	tags,
	disableAutoplay,
	hideAttribution,
	attributionClassName,
	mediaRef,
	crossOrigin,
}: CosmosVideoProps): React.JSX.Element | null => {
	const { url, mimeType, fallbackImage, attribution: videoAttribution } = video as CosmosAssetFragment;
	const hygraphImageLoader = useHygraphImageLoader();
	const disabledHygraphImageLoader = useDisabledHygraphImageLoader();
	const attribution = !hideAttribution && !!videoAttribution ? videoAttribution : undefined;

	// SVG fallbacks can't be transformed — keep the disabled loader; otherwise route through
	// the responsive loader so srcset entries actually resolve to per-width CDN variants
	// instead of returning the raw asset URL for every entry.
	const isFallbackSvg = fallbackImage?.mimeType?.includes("svg") ?? false;
	const fallbackLoader = isFallbackSvg ? disabledHygraphImageLoader : hygraphImageLoader;

	const posterUrl = useMemo(() => {
		const src = fallbackImage?.optUrl || fallbackImage?.url;
		if (!src || isFallbackSvg) return src || undefined;
		return hygraphImageLoader({ src, width: POSTER_WIDTH }) || undefined;
	}, [fallbackImage?.optUrl, fallbackImage?.url, isFallbackSvg, hygraphImageLoader]);

	const [showImg, setShowImg] = useState(Boolean(!url || !mimeType?.includes("video")));

	const videoRef = useRef<HTMLSourceElement>(null);
	const videoElRef = useCallback(
		(el: HTMLVideoElement | null) => {
			mediaRef?.(el);
		},
		[mediaRef],
	);
	const videoSrcAttrs = mimeType ? { type: mimeType } : {};

	useEffect(() => {
		if (videoRef.current) {
			const videoElem = videoRef.current;
			videoElem.addEventListener("error", () => setShowImg(true));
		}
	}, [videoRef]);

	const fallbackImageEl = (
		<CosmosImage
			className={cx("cosmos-video-fallback-image", className, css({ objectFit: "cover", w: "full" }))}
			src={fallbackImage?.optUrl || fallbackImage?.url || ""}
			width={fallbackImage?.width || 0}
			height={fallbackImage?.height || 0}
			alt={fallbackImage?.altText || ""}
			loading="lazy"
			loader={fallbackLoader}
			sizes="100vw"
		/>
	);

	const fallbackWithAttr = attribution ? (
		<CosmosTagsAndAttribution tags={tags} attribution={attribution} className={attributionClassName}>
			{fallbackImageEl}
		</CosmosTagsAndAttribution>
	) : (
		fallbackImageEl
	);

	return (
		<>
			{showImg ? (
				!fallbackImage ? undefined : (
					fallbackWithAttr
				)
			) : (
				<CosmosTagsAndAttribution tags={tags} attribution={attribution} className={attributionClassName}>
					<video
						ref={videoElRef}
						key={`cosmos-video-${url}`}
						className={cx("cosmos-video", className, css({ objectFit: "cover", aspectRatio: "16 / 9" }))}
						title={video?.altText || ""}
						controls={!!video?.enableVideoControls}
						crossOrigin={crossOrigin}
						playsInline
						disableRemotePlayback
						autoPlay={!disableAutoplay}
						muted
						loop
						tabIndex={-1}
						poster={posterUrl}
					>
						<source ref={videoRef} src={url} {...videoSrcAttrs} />
						{fallbackImage?.optUrl && fallbackImage?.mimeType ? fallbackImageEl : null}
					</video>
				</CosmosTagsAndAttribution>
			)}
		</>
	);
};
